import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../axiosInstance";
import {
    TitleRow,
    ToolbarRow,
    TableDocs,
    CostMarginModal,
    ExportModal,
    persistEtatFacture,
    totalsWithCost,
    normalizeEtatKey,
    num,
    toMonthKey,
    toMonthLabel,
} from "./centre/SharedCentre";

export default function DocumentsCentreFactures() {
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const [exportOpen, setExportOpen] = useState(false);

    // filters + sort (spécifique factures)
    const [filters, setFilters] = useState({
        etat: "",
        client: "",
        from: "",
        to: "",
        minHT: "",
        maxHT: "",
        onlyPaid: false,
        // ftype: "", // ← décommente si tu veux filtrer par type (acompte/partielle/finale)
    });
    const [sort, setSort] = useState({ key: "date", dir: "desc" });

    // sélection multi
    const [selected, setSelected] = useState(new Set());

    // modal coût
    const [costOpen, setCostOpen] = useState(false);
    const [costDoc, setCostDoc] = useState(null);

    const navigate = useNavigate();

    // chargement
    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const res = await axiosInstance.get("/factures");
                const sorted = (res.data || [])
                    .slice()
                    .sort(
                        (a, b) =>
                            new Date(b.date || b.date_facture || b.created_at) -
                            new Date(a.date || a.date_facture || a.created_at) ||
                            (b.id ?? 0) - (a.id ?? 0)
                    );
                if (mounted) setRows(sorted);
            } catch (e) {
                console.error("Chargement factures échoué:", e);
            } finally {
                if (mounted) setLoading(false);
            }
        })();
        return () => {
            mounted = false;
        };
    }, []);

    // clients pour datalist
    const clientsSet = useMemo(() => {
        const s = new Set();
        for (const f of rows) s.add(f.client_nom || f.client?.nom || "");
        return Array.from(s)
            .filter(Boolean)
            .sort((a, b) =>
                String(a).localeCompare(String(b), "fr", { sensitivity: "base" })
            );
    }, [rows]);

    const textMatch = (hay, needle) =>
        String(hay || "").toLowerCase().includes(String(needle).toLowerCase());

    const inDateRange = (iso) => {
        if (!filters.from && !filters.to) return true;
        const t = iso ? new Date(iso).getTime() : NaN;
        if (Number.isNaN(t)) return false;
        if (filters.from && t < new Date(filters.from).getTime()) return false;
        if (filters.to && t > new Date(filters.to).getTime() + 24 * 3600 * 1000 - 1)
            return false;
        return true;
    };

    const inAmountRange = (ht) => {
        const min = Number(filters.minHT || NaN);
        const max = Number(filters.maxHT || NaN);
        if (!Number.isFinite(min) && !Number.isFinite(max)) return true;
        if (Number.isFinite(min) && ht < min) return false;
        if (Number.isFinite(max) && ht > max) return false;
        return true;
    };

    // filtre
    const filtered = useMemo(() => {
        return rows.filter((f) => {
            const numero = f.numero || f.numero_facture;
            const nom = f.client_nom || f.client?.nom;
            const adr = f.client_adresse || f.client?.adresse;
            const projet = f.projet_nom || f.projet || f.nom_projet || f.titre || f.objet;

            const okQ = !q || [numero, nom, adr, projet].some((v) => textMatch(v, q));
            if (!okQ) return false;

            if (filters.client && !textMatch(nom, filters.client)) return false;

            if (
                filters.etat &&
                normalizeEtatKey(f.etat || f.status) !== normalizeEtatKey(filters.etat)
            ) {
                return false;
            }

            // if (filters.ftype && String(f.type || f.kind || f.nature || "").toLowerCase() !== String(filters.ftype).toLowerCase()) return false;

            const { totalHT } = totalsWithCost(f, num(f.total_ht ?? f.montant_ht));
            if (!inAmountRange(totalHT)) return false;

            const refDate = f.date || f.date_facture || f.created_at;
            if (!inDateRange(refDate)) return false;

            if (filters.onlyPaid) {
                const nk = normalizeEtatKey(f.etat || f.status);
                if (!(nk === "payée" || nk === "payee")) return false;
            }

            return true;
        });
    }, [rows, q, filters]);

    // tri
    const sortFn = (a, b) => {
        const dir = sort.dir === "asc" ? 1 : -1;
        const get = (x) => {
            const totalTTC = num(x.total_ttc ?? x.montant_ttc ?? x.total);
            const { totalHT, totalCost, marge, taux } = totalsWithCost(
                x,
                num(x.total_ht ?? x.montant_ht)
            );
            const numero = x.numero || x.numero_facture;
            const nom = x.client_nom || x.client?.nom;
            const date = x.date || x.date_facture || x.created_at;

            // PU moyen
            const lignes = Array.isArray(x.contenu)
                ? x.contenu
                : x.contenu
                    ? JSON.parse(x.contenu)
                    : Array.isArray(x.lignes)
                        ? x.lignes
                        : x.lignes
                            ? JSON.parse(x.lignes)
                            : [];
            const q = lignes.reduce(
                (s, l) => s + (Number(l.quantite ?? l.qty ?? 1) || 1),
                0
            );
            const pu = q > 0 ? totalHT / q : totalHT;

            switch (sort.key) {
                case "date":
                    return new Date(date).getTime();
                case "numero":
                    return String(numero || "");
                case "client":
                    return String(nom || "");
                case "ht":
                    return totalHT;
                case "cost":
                    return totalCost;
                case "marge":
                    return marge;
                case "taux":
                    return taux;
                case "ttc":
                    return totalTTC;
                case "pu":
                    return pu;
                default:
                    return new Date(date).getTime();
            }
        };
        const A = get(a),
            B = get(b);
        if (typeof A === "string" || typeof B === "string") {
            return dir * String(A).localeCompare(String(B), "fr", { sensitivity: "base" });
        }
        return dir * (A - B);
    };

    const filteredSorted = useMemo(
        () => filtered.slice().sort(sortFn),
        [filtered, sort]
    );

    // groupage par mois
    const groups = useMemo(() => {
        const map = new Map();
        for (const f of filteredSorted) {
            const key = toMonthKey(f.date || f.date_facture || f.created_at);
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(f);
        }
        return Array.from(map.entries()).sort((a, b) => b[0].localeCompare(a[0]));
    }, [filteredSorted]);

    // sélection
    const toggleOne = (id) => {
        setSelected((s) => {
            const n = new Set(s);
            n.has(id) ? n.delete(id) : n.add(id);
            return n;
        });
    };

    // interactions ligne
    const onRowClick = (doc) => navigate(`/afficher-facture?id=${doc.id}`);

    const onEtatChangeRow = async (id, newEtat) => {
        setRows((prev) => prev.map((f) => (f.id === id ? { ...f, etat: newEtat } : f)));
        try {
            await persistEtatFacture(id, newEtat);
            localStorage.setItem("docs:refresh", String(Date.now()));
        } catch (e) {
            console.warn("Maj état facture KO:", e);
        }
    };

    // modal coût
    const openCost = (doc) => {
        setCostDoc(doc);
        setCostOpen(true);
    };
    const onCostSaved = (newCost) => {
        setRows((prev) =>
            prev.map((f) =>
                f.id === costDoc.id
                    ? { ...f, meta: { ...(f.meta || {}), cout_total: newCost } }
                    : f
            )
        );
    };

    // KPIs
    const kpis = useMemo(() => {
        const acc = filteredSorted.reduce(
            (a, x) => {
                const { totalHT, totalCost, marge } = totalsWithCost(
                    x,
                    num(x.total_ht ?? x.montant_ht)
                );
                a.ht += totalHT;
                a.cost += totalCost;
                a.marge += Math.max(0, marge);
                return a;
            },
            { ht: 0, cost: 0, marge: 0 }
        );
        return { ht: acc.ht, marge: acc.marge, taux: acc.ht > 0 ? acc.marge / acc.ht : 0 };
    }, [filteredSorted]);

    if (loading) return <p className="text-center py-10">⏳ Chargement…</p>;

    return (
        <div className="p-1 space-y-4">
            <TitleRow title="Factures" kpis={kpis} />

            <ToolbarRow
                q={q}
                setQ={setQ}
                tab="factures"
                filters={filters}
                setFilters={setFilters}
                uniqueClients={clientsSet}
                sort={sort}
                setSort={setSort}
                onExport={() => setExportOpen(true)}
                onCreateDevis={() => navigate("/creer-devis")}
                onCreateFacture={() => navigate("/creer-facture")}

            />

            {groups.length === 0 && (
                <p className="text-center py-8 text-gray-500">Aucun document.</p>
            )}

            {groups.map(([monthKey, monthRows]) => (
                <section key={monthKey} className="space-y-2">
                    <div className="sticky top-0 z-20 bg-slate-100/80 backdrop-blur px-2 py-1 rounded border text-sm font-semibold capitalize">
                        {toMonthLabel(monthKey)}
                    </div>

                    <TableDocs
                        kind="facture"
                        rows={monthRows}
                        onRowClick={onRowClick}
                        onEtatChange={onEtatChangeRow}
                        onOpenCost={openCost}
                        selection={{
                            ids: selected,
                            allChecked:
                                monthRows.length > 0 && monthRows.every((r) => selected.has(r.id)),
                        }}
                        toggleOne={toggleOne}
                        toggleAll={() => {
                            const ids = monthRows.map((r) => r.id);
                            const all = ids.every((id) => selected.has(id));
                            setSelected((prev) => {
                                const n = new Set(prev);
                                if (all) ids.forEach((id) => n.delete(id));
                                else ids.forEach((id) => n.add(id));
                                return n;
                            });
                        }}
                        sortable={(key) =>
                            setSort((s) => ({
                                key,
                                dir: s.key === key ? (s.dir === "asc" ? "desc" : "asc") : "asc",
                            }))
                        }
                    />
                </section>
            ))}

            <ExportModal
                open={exportOpen}
                onClose={() => setExportOpen(false)}
                tab="factures"
                devis={[]}
                factures={filteredSorted}
            />

            <CostMarginModal
                open={costOpen}
                onClose={() => setCostOpen(false)}
                kind="facture"
                doc={costDoc || {}}
                onSaved={onCostSaved}
            />
        </div>
    );
}
